import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { navigateCallbackMap } from '../constants/navigateCallbackMap';
import { NavigateWithCallback } from '../types/NavigateWithCallback';

/**
 * Custom hook that wraps react-router's navigate to support post-navigation callbacks.
 * Uses location change detection to trigger callbacks after navigation completes.
 * Callbacks are stored globally to survive component unmounting during navigation.
 *
 * @example
 * const navigateWithCallback = useNavigateWithCallback();
 *
 * navigateWithCallback('/next-page').then(() => {
 *   // This runs after navigation completes
 *   resetStore(useApplyStore);
 * });
 */
export function useNavigateWithCallback(): NavigateWithCallback {
  const navigate = useNavigate();
  const location = useLocation();
  const previousPathnameRef = useRef(location.pathname);

  useEffect((): void => {
    const hasNavigated = location.pathname !== previousPathnameRef.current;
    const hasPendingCallback = navigateCallbackMap.has(location.pathname);

    // Early return if no navigation occurred or no callback pending
    if (!hasNavigated || !hasPendingCallback) {
      return;
    }

    // Execute callback
    const callback = navigateCallbackMap.get(location.pathname);

    if (callback) {
      callback();
    }

    // Remove the callback for this path
    navigateCallbackMap.delete(location.pathname);

    // Update previous pathname for next comparison
    previousPathnameRef.current = location.pathname;
  }, [location.pathname]);

  return (to, options) => ({
    then: (callback: () => void): void => {
      // Store callback globally keyed by destination path
      navigateCallbackMap.set(to, callback);

      navigate(to, options);
    },
  });
}
